/**
 * States
 * idle -> loading(success) -> allocated(control, variation-x, ...)
 * loading(fail) -> allocated(control)
 */

import { OptimizelyContext } from '@optimizely/react-sdk'
import { Entry } from 'contentful'
import { useContext, useEffect, useState } from 'react'
import { contentfulClient } from '../api/contentful'

export enum StatusTypes {
  idle = 'idle',
  loading = 'loading',
  allocated = 'allocated',
}
interface Status<Payload = any> {
  type: StatusTypes
  payload?: Payload
  error?: Error
}

interface VariationContainer<Variation> {
  experimentKey: string
  experimentId: string
  experimentTitle: string
  meta: {
    [variationKey: string]: string
  }
  variations: Entry<Variation>[]
}

const pullVariationEntry = <ContentType>(
  experimentEntry: Entry<VariationContainer<ContentType>>,
  optimizelyVariationId: string
) => {
  const variationEntryId = experimentEntry.fields.meta[optimizelyVariationId]
  const { variations } = experimentEntry.fields
  return variations.find((item) => item.sys.id === variationEntryId)
}

export const useContentfulOptimizelyExperiment = <Variation = any>(
  experimentEntryId: string
) => {
  const { optimizely, timeout, isServerSide } = useContext(OptimizelyContext)
  const [status, setStatus] = useState<Status<Entry<Variation>>>({
    type: isServerSide || !experimentEntryId ? StatusTypes.allocated : StatusTypes.idle,
  })

  useEffect(() => {
    if (optimizely) return

    const cannotAllocate = () => {
      setStatus({
        type: StatusTypes.allocated,
        error: new Error(
          'useContentfulOptimizelyExperiment: Optimizely not available. Default to control.'
        ),
      })
    }

    if (timeout) {
      const waitingId = setTimeout(cannotAllocate, timeout)
      return () => {
        clearTimeout(waitingId)
      }
    }
    if (!timeout) {
      cannotAllocate()
    }
  }, [optimizely, timeout])

  useEffect(() => {
    if (!optimizely || !experimentEntryId) return

    contentfulClient
      .getEntry<VariationContainer<Variation>>(experimentEntryId)
      .then((experimentEntry) => {
        const variationId = optimizely.activate(experimentEntry.fields.experimentKey)
        const variationEntry = pullVariationEntry<Variation>(experimentEntry, variationId)
        setStatus({
          type: StatusTypes.allocated,
          payload: variationEntry,
        })
      })
      .catch((error) => {
        setStatus({ type: StatusTypes.allocated, error })
      })
    setStatus({ type: StatusTypes.loading })
  }, [optimizely, experimentEntryId])

  return status
}
